import React, { useState, useEffect } from 'react';
import { FiUsers, FiUserPlus, FiUserCheck, FiTrendingUp, FiLoader } from 'react-icons/fi';
import AdminLayout from '../../components/admin/layout/AdminLayout';
import axios from 'axios';

interface UserStats {
  total_users: number;
  active_users: number;
  verified_users: number;
  verification_rate: number;
  users_by_role: {
    ADMIN: number;
    MEMBER: number;
  };
  recent_registrations: number;
  login_activity: {
    recent_logins_7d: number;
    login_rate: number;
  };
}

interface AnalyticsOverview {
  users: {
    total: number;
    active: number;
    new_count: number;
    change_percent: number;
  };
}

const UserAnalytics: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [analyticsOverview, setAnalyticsOverview] = useState<AnalyticsOverview | null>(null);
  const [dateRange, setDateRange] = useState<'1d' | '7d' | '30d' | '90d' | '1y'>('7d');

  useEffect(() => {
    fetchUserAnalytics();
  }, [dateRange]);

  const fetchUserAnalytics = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('authToken');

      if (!token) {
        throw new Error('ไม่พบ authentication token');
      }

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Fetch user stats
      const statsResponse = await axios.get<{ success: boolean; data: UserStats }>(
        `${import.meta.env.VITE_API_URL}/api/admin/users/stats`,
        { headers }
      );

      // Fetch analytics overview for user metrics
      const analyticsResponse = await axios.get<{ success: boolean; data: AnalyticsOverview }>(
        `${import.meta.env.VITE_API_URL}/api/admin/analytics/overview?date_range=${dateRange}`,
        { headers }
      );

      if (statsResponse.data.success) {
        setUserStats(statsResponse.data.data);
      }

      if (analyticsResponse.data.success) {
        setAnalyticsOverview(analyticsResponse.data.data);
      }

    } catch (err: any) {
      console.error('Failed to fetch user analytics:', err);
      setError(err.response?.data?.detail || err.message || 'ไม่สามารถโหลดข้อมูลสถิติผู้ใช้ได้');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <FiLoader className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">กำลังโหลดข้อมูลสถิติผู้ใช้...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="max-w-4xl mx-auto px-6 py-12">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <p className="text-red-600 font-semibold mb-2">เกิดข้อผิดพลาด</p>
            <p className="text-red-500 text-sm">{error}</p>
            <button
              onClick={fetchUserAnalytics}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              ลองใหม่อีกครั้ง
            </button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">สถิติผู้ใช้งาน</h1>
              <p className="text-gray-600">ข้อมูลและการวิเคราะห์ผู้ใช้งานในระบบ</p>
            </div>

            {/* Date Range Selector */}
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">ช่วงเวลา:</label>
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value as any)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="1d">1 วัน</option>
                <option value="7d">7 วัน</option>
                <option value="30d">30 วัน</option>
                <option value="90d">90 วัน</option>
                <option value="1y">1 ปี</option>
              </select>
            </div>
          </div>
        </div>

        {/* Overview Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Users */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-blue-100 rounded-lg p-3">
                <FiUsers className="h-6 w-6 text-blue-600" />
              </div>
              {analyticsOverview && (
                <span className={`text-sm font-semibold ${analyticsOverview.users.change_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {analyticsOverview.users.change_percent >= 0 ? '+' : ''}{analyticsOverview.users.change_percent}%
                </span>
              )}
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-1">
              {userStats?.total_users.toLocaleString() || 0}
            </h3>
            <p className="text-sm text-gray-600">ผู้ใช้ทั้งหมด</p>
          </div>

          {/* Active Users */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-green-100 rounded-lg p-3">
                <FiUserCheck className="h-6 w-6 text-green-600" />
              </div>
              {userStats && (
                <span className="text-sm font-semibold text-blue-600">
                  {userStats.verification_rate.toFixed(1)}%
                </span>
              )}
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-1">
              {userStats?.active_users.toLocaleString() || 0}
            </h3>
            <p className="text-sm text-gray-600">ผู้ใช้ที่ใช้งานอยู่</p>
            <p className="text-xs text-gray-500 mt-1">
              {userStats && userStats.total_users > 0
                ? `${((userStats.active_users / userStats.total_users) * 100).toFixed(1)}%`
                : '0%'} ของทั้งหมด
            </p>
          </div>

          {/* New Users */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-purple-100 rounded-lg p-3">
                <FiUserPlus className="h-6 w-6 text-purple-600" />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-1">
              {analyticsOverview?.users.new_count.toLocaleString() || 0}
            </h3>
            <p className="text-sm text-gray-600">ผู้ใช้ใหม่ ({dateRange})</p>
          </div>

          {/* Recent Logins */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-orange-100 rounded-lg p-3">
                <FiTrendingUp className="h-6 w-6 text-orange-600" />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-1">
              {userStats?.login_activity.recent_logins_7d.toLocaleString() || 0}
            </h3>
            <p className="text-sm text-gray-600">เข้าสู่ระบบ (7 วัน)</p>
            <p className="text-xs text-gray-500 mt-1">
              อัตรา {userStats?.login_activity.login_rate.toFixed(1) || '0.0'}%
            </p>
          </div>
        </div>

        {/* Detailed Stats Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* User Role Distribution */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">การกระจายตาม Role</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-blue-600 rounded-full"></div>
                  <span className="text-sm text-gray-700">MEMBER</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-gray-900">
                    {userStats?.users_by_role.MEMBER.toLocaleString() || 0}
                  </span>
                  <span className="text-xs text-gray-500">
                    {userStats && userStats.total_users > 0
                      ? `${((userStats.users_by_role.MEMBER / userStats.total_users) * 100).toFixed(1)}%`
                      : '0%'}
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-purple-600 rounded-full"></div>
                  <span className="text-sm text-gray-700">ADMIN</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-gray-900">
                    {userStats?.users_by_role.ADMIN.toLocaleString() || 0}
                  </span>
                  <span className="text-xs text-gray-500">
                    {userStats && userStats.total_users > 0
                      ? `${((userStats.users_by_role.ADMIN / userStats.total_users) * 100).toFixed(1)}%`
                      : '0%'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* User Activity Stats */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">สถิติการใช้งาน</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">ผู้ใช้ใหม่ล่าสุด</span>
                <span className="text-sm font-semibold text-gray-900">
                  {userStats?.recent_registrations.toLocaleString() || 0} คน
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">เข้าสู่ระบบ (7 วัน)</span>
                <span className="text-sm font-semibold text-gray-900">
                  {userStats?.login_activity.recent_logins_7d.toLocaleString() || 0} ครั้ง
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">อัตราการเข้าสู่ระบบ</span>
                <span className="text-sm font-semibold text-gray-900">
                  {userStats?.login_activity.login_rate.toFixed(1) || '0.0'}%
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">อัตราการยืนยันอีเมล</span>
                <span className="text-sm font-semibold text-gray-900">
                  {userStats?.verification_rate.toFixed(1) || '0.0'}%
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* User Status Summary */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">สรุปสถานะผู้ใช้</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-3xl font-bold text-green-600 mb-1">
                {userStats?.active_users.toLocaleString() || 0}
              </p>
              <p className="text-sm text-gray-600">ผู้ใช้ Active</p>
              <p className="text-xs text-gray-500 mt-1">
                {userStats && userStats.total_users > 0
                  ? `${((userStats.active_users / userStats.total_users) * 100).toFixed(1)}%`
                  : '0%'}
              </p>
            </div>

            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-3xl font-bold text-gray-600 mb-1">
                {userStats && userStats.total_users > 0
                  ? (userStats.total_users - userStats.active_users).toLocaleString()
                  : 0}
              </p>
              <p className="text-sm text-gray-600">ผู้ใช้ Inactive</p>
              <p className="text-xs text-gray-500 mt-1">
                {userStats && userStats.total_users > 0
                  ? `${(((userStats.total_users - userStats.active_users) / userStats.total_users) * 100).toFixed(1)}%`
                  : '0%'}
              </p>
            </div>

            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="text-3xl font-bold text-blue-600 mb-1">
                {userStats?.total_users.toLocaleString() || 0}
              </p>
              <p className="text-sm text-gray-600">ผู้ใช้ทั้งหมด</p>
              <p className="text-xs text-gray-500 mt-1">
                ยืนยันอีเมล {userStats?.verified_users.toLocaleString() || 0} คน
              </p>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default UserAnalytics;
